/** TodoCard.tsx — 일일 업무 카드 (목록/팝업 공용)
 *  레이아웃: 제목 우측에 메모/첨부 인디케이터, 클릭 시 펼침
 */
import { useState } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { Todo } from '../types';
import type { Staff } from './AssigneeChipPicker';
import TodoCheckBox from './TodoCheckBox';
import { stageOf } from '../todoCheckView';

interface Props {
  todo: Todo;
  staff: Staff[];
  myId: string;
  onChange: (updated: Todo) => void;
  onDelete?: (id: string) => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
  compact?: boolean;
  onOpenDetail?: (id: string) => void;
}

function dDay(due: string | null): { label: string; color: string; bg: string } | null {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(due); target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { label: `+${-days}일 지남`, color: C.white, bg: C.red };
  if (days === 0) return { label: 'D-day', color: C.white, bg: C.red };
  if (days === 1) return { label: 'D-1', color: C.white, bg: C.orange };
  if (days <= 3) return { label: `D-${days}`, color: C.orange, bg: C.orangeLt };
  return { label: `D-${days}`, color: C.sub, bg: C.bg };
}

function isNew(t: Todo): boolean {
  const created = new Date(t.created_at).getTime();
  return Date.now() - created < 86400000;
}

/** 새 댓글 깜빡임 — 테두리 · 알약이 1.2초마다 숨쉬듯(눈에 띄되 어지럽지 않게) · 움직임 줄이기 설정이면 멈춤 */
const UNREAD_CSS = `@keyframes todoUnreadPulse{0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.45)}50%{box-shadow:0 0 0 6px rgba(220,38,38,0)}}
@keyframes todoUnreadBlink{0%,100%{opacity:1}50%{opacity:.45}}
.todo-unread{animation:todoUnreadPulse 1.2s ease-in-out infinite}
.todo-unread-pill{animation:todoUnreadBlink 1.2s ease-in-out infinite}
@media (prefers-reduced-motion: reduce){.todo-unread,.todo-unread-pill{animation:none}}`;

export default function TodoCard({ todo, staff, myId, onChange, onDelete, onToast, compact, onOpenDetail }: Props) {
  const [memoOpen, setMemoOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [askDel, setAskDel] = useState(false);
  const stage = stageOf(todo, myId);
  // 내 차례인 결과 확인 단계는 테두리로 눈에 띄게(확인할 차례 · 안내할 차례 · 답 적을 차례)
  const myTurn = stage === 'check' || stage === 'bossNotify' || stage === 'staffNotify';

  const dd = dDay(todo.due_date);
  const myAssignees = Array.isArray(todo.assignees) && todo.assignees.length > 0
    ? todo.assignees : [todo.assigned_to];
  const assigneeStaff = myAssignees.map(id => staff.find(s => s.id === id)).filter(Boolean) as Staff[];
  const iAmAssignee = myAssignees.includes(myId);
  const iAmCreator = todo.created_by === myId;
  const newFlag = isNew(todo) && todo.status === 'pending';
  const hasMemo = !!todo.description;
  const attachments = Array.isArray(todo.attachments) ? todo.attachments : [];
  const hasAttach = attachments.length > 0;

  async function setStatus(status: 'pending' | 'partial' | 'done', extras: Partial<Todo> = {}) {   // 결과 확인을 받는 일은 TodoCheckBox 가 맡는다
    if (busy) return;
    setBusy(true);
    try {
      const r = await todosApi.updateTodo(todo.id, { status, ...extras });
      if (r.success && r.data) onChange(r.data);
      else onToast?.(r.error || '저장 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '저장 실패', 'error'); }
    finally { setBusy(false); }
  }

  // 새 댓글(2026-10-27 지도사 「댓글이 달리면 상대방에게 보이도록 · 그냥 안 보거든 · 깜빡깜빡」) — 카드 테두리가 깜빡이고 💬 자리에 「새 댓글 n」
  const unread = typeof todo.unread_comments === 'number' ? todo.unread_comments : 0;
  return (
    <div className={unread > 0 ? 'todo-unread' : undefined} style={{
      background: C.white,
      border: `${myTurn || unread > 0 ? 2 : 1}px solid ${unread > 0 ? C.red : myTurn ? C.text : todo.status === 'partial' ? C.orange : C.border}`,
      borderRadius: R.md, padding: '11px 13px', marginBottom: 8,
      position: 'relative',
    }}>
      {unread > 0 && <style>{UNREAD_CSS}</style>}
      {newFlag && <div title="새 항목" style={{ position: 'absolute', top: 8, left: -3,
        width: 6, height: 6, borderRadius: '50%', background: C.accent }} />}

      {/* 제목 행: 제목 + 우측 메모/첨부 인디케이터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div onClick={() => onOpenDetail?.(todo.id)} style={{
          cursor: onOpenDetail ? 'pointer' : 'default',
          fontSize: T.md, fontWeight: 600, color: C.text, flex: 1, minWidth: 0,
          textDecoration: todo.status === 'done' ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {todo.title}
        </div>
        {hasMemo && (
          <button onClick={(e) => { e.stopPropagation(); setMemoOpen(v => !v); }}
            title={memoOpen ? '메모 닫기' : '메모 보기'}
            style={{
              border: 'none', background: 'transparent',
              color: memoOpen ? C.text : C.muted,
              cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
              fontSize: 14, lineHeight: 1,
              display: 'inline-flex', alignItems: 'center',
            }}>📝</button>
        )}
        {hasAttach && (
          <button onClick={(e) => { e.stopPropagation(); setAttachOpen(v => !v); }}
            title={attachOpen ? '첨부 닫기' : `첨부 ${attachments.length}개 보기`}
            style={{
              border: 'none', background: 'transparent',
              color: attachOpen ? C.accent : C.muted,
              cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
              fontSize: 13, lineHeight: 1, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 2,
            }}>
            📎 <span style={{ fontSize: 11 }}>{attachments.length}</span>
          </button>
        )}
        {unread > 0 && (
          <button onClick={(e) => { e.stopPropagation(); onOpenDetail?.(todo.id); }} className="todo-unread-pill"
            title={`새 댓글 ${unread}개 — 눌러서 보기`}
            style={{ border: 'none', background: C.red, color: '#fff', cursor: 'pointer', padding: '3px 9px', borderRadius: 12, flexShrink: 0, fontSize: 12, fontWeight: 800, lineHeight: 1.3, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}>
            💬 새 댓글 {unread}
          </button>
        )}
        {unread === 0 && typeof todo.comment_count === 'number' && todo.comment_count > 0 && (
          <button onClick={(e) => { e.stopPropagation(); onOpenDetail?.(todo.id); }}
            title={`댓글 ${todo.comment_count}개`}
            style={{
              border: 'none', background: 'transparent', color: C.accent,
              cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
              fontSize: 13, lineHeight: 1, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 2,
            }}>
            💬 <span style={{ fontSize: 11 }}>{todo.comment_count}</span>
          </button>
        )}
      </div>

      {/* 메타: D-day, 날짜, 담당자 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: T.xs, color: C.muted }}>
        {dd && (
          <span style={{ padding: '2px 7px', borderRadius: R.sm,
            background: dd.bg, color: dd.color, fontWeight: 700, fontSize: T.xs }}>{dd.label}</span>
        )}
        {todo.due_date && <span>{String(todo.due_date).slice(5, 10).replace('-', '/')}</span>}
        {assigneeStaff.length > 0 && (
          <span style={{
            padding: '2px 8px', borderRadius: R.sm,
            background: iAmCreator && !iAmAssignee ? '#FFF7ED' : C.bgAlt,
            color: iAmCreator && !iAmAssignee ? C.orange : C.sub,
            fontWeight: 600,
          }}>
            {iAmCreator && !iAmAssignee ? '→ ' : '담당 '}
            {assigneeStaff.map(a => a.id === myId ? `${a.name} (나)` : a.name).join(', ')}
            {iAmCreator && !iAmAssignee ? '에게 의뢰' : ''}
          </span>
        )}
      </div>

      {/* 메모 펼침 */}
      {memoOpen && hasMemo && (
        <div style={{ marginTop: 6, padding: '8px 11px',
          fontSize: T.xs, color: C.sub, background: C.bgAlt, borderRadius: R.sm,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            marginBottom: 4, alignItems: 'center' }}>
            <span style={{ fontSize: T.xs, fontWeight: 700, color: C.text }}>📝 메모</span>
            <button onClick={(e) => { e.stopPropagation(); setMemoOpen(false); }}
              style={{ border: 'none', background: 'transparent',
                color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>▲ 닫기</button>
          </div>
          {todo.description}
        </div>
      )}

      {/* 첨부 펼침 */}
      {attachOpen && hasAttach && (
        <div style={{ marginTop: 6, padding: '6px 9px',
          background: C.bgAlt, borderRadius: R.sm }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 5 }}>
            <span style={{ fontSize: T.xs, fontWeight: 700, color: C.text }}>
              📎 첨부 ({attachments.length})
            </span>
            <button onClick={(e) => { e.stopPropagation(); setAttachOpen(false); }}
              style={{ border: 'none', background: 'transparent',
                color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>▲ 닫기</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {attachments.map(att => (
              <button key={att.id}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const r = await todosApi.getAttachmentUrl(todo.id, att.id);
                    if (r.success && r.data?.url) window.open(r.data.url, '_blank');
                  } catch { /* ignore */ }
                }}
                style={{ padding: '3px 9px', borderRadius: R.sm,
                  border: `1px solid ${C.accent}`, background: C.white,
                  color: C.accent, fontSize: T.xs, fontWeight: 600,
                  cursor: 'pointer', maxWidth: 220, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={att.fileName}>
                📎 {att.fileName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 결과 확인을 받는 일(2026-10-22) — 직원: 답 기다려요 · 답 적기 / 지도사: 직원이 안내 · 내가 안내 */}
      {todo.needs_check && <TodoCheckBox todo={todo} staff={staff} myId={myId} onChange={onChange} onToast={onToast} />}

      {/* 액션: 가로 세그먼트 또는 done 버튼 */}
      {!todo.needs_check && todo.status !== 'done' && (
        <div style={{ display: 'inline-flex', marginTop: 10,
          border: `1px solid ${C.border}`, borderRadius: R.sm, overflow: 'hidden' }}>
          <SegBtn label="시작 전" act={todo.status === 'pending'} disabled={busy}
            onClick={() => setStatus('pending', { progress: 0, progress_note: null })} />
          <SegBtn label="진행 중" act={todo.status === 'partial'} disabled={busy}
            onClick={() => setStatus('partial', { progress: 50 })} />
          <SegBtn label="완료" act={false} disabled={busy} last
            onClick={() => setStatus('done', { progress: 100 })} />
        </div>
      )}
      {todo.status === 'done' && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {(!todo.needs_check || iAmCreator) && <button onClick={() => setStatus('pending', { progress: 0, progress_note: null })} disabled={busy}
            style={{ padding: '5px 12px', borderRadius: R.sm,
              border: `1px solid ${C.accent}`, background: C.white,
              color: C.accent, fontSize: T.xs, fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            ↺ 미완료로 되돌리기
          </button>}
          {onDelete && (askDel
            ? <>
                <button onClick={() => onDelete(todo.id)}
                  style={{ padding: '5px 11px', borderRadius: R.sm, border: `1px solid ${C.red}`,
                    background: C.white, color: C.red, fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>정말 삭제</button>
                <button onClick={() => setAskDel(false)}
                  style={{ padding: '5px 11px', borderRadius: R.sm, border: `1px solid ${C.border}`,
                    background: C.white, color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>그만두기</button>
              </>
            : <button onClick={() => setAskDel(true)}
                style={{ padding: '5px 11px', borderRadius: R.sm, border: `1px solid ${C.border}`,
                  background: C.white, color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>
                🗑 삭제
              </button>
          )}
        </div>
      )}
    </div>
  );
}

function SegBtn({ label, act, onClick, disabled, last }: {
  label: string; act: boolean; onClick: () => void; disabled?: boolean; last?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: '6px 18px', border: 'none',
        borderRight: last ? 'none' : `1px solid ${C.border}`,
        background: act ? C.text : C.white,
        color: act ? C.white : C.sub,
        fontSize: T.sm, fontWeight: act ? 700 : 500,
        cursor: disabled ? 'wait' : 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s, color 0.15s',
      }}>
      {label}
    </button>
  );
}
